import type {
  CheckoutBundleProductPayload,
  CheckoutUpsellPayload,
  CheckoutUpsellResponse,
  PopupBundleProductDto,
  PopupProductDto,
} from "@/types/api.types";

function buildFallbackBundleProducts(
  product: PopupProductDto,
): CheckoutBundleProductPayload[] {
  return [
    {
      bp_konnektive_id: "",
      bp_sku: "",
      bp_price: product.price.value,
      bp_amount: product.amount,
      primary_product: true,
    },
  ];
}

function normalizeBundleProducts(
  bundleProducts: PopupBundleProductDto[],
  product: PopupProductDto,
): CheckoutBundleProductPayload[] {
  if (!bundleProducts.length) {
    return buildFallbackBundleProducts(product);
  }

  return bundleProducts.map((bundleProduct) => ({
    bp_konnektive_id: bundleProduct.konnektive_id.trim(),
    bp_sku: bundleProduct.sku.trim(),
    bp_price: Number(bundleProduct.price) || 0,
    bp_amount: Number(bundleProduct.amount) || 1,
    primary_product: bundleProduct.primary_product,
  }));
}

export function buildUpsellPayload(
  product: PopupProductDto,
  orderId: string,
  postId: number,
): CheckoutUpsellPayload {
  const bundleProducts = normalizeBundleProducts(product.bundle_products, product);
  const primaryProduct =
    bundleProducts.find((bundleProduct) => bundleProduct.primary_product) ??
    bundleProducts[0];

  return {
    orderId,
    postId,
    upsell: 1,
    upsellType: "upsell",
    sku: primaryProduct.bp_sku,
    konnektive_id: primaryProduct.bp_konnektive_id,
    price: primaryProduct.bp_price,
    quantity: primaryProduct.bp_amount,
    totalAmount: primaryProduct.bp_price * primaryProduct.bp_amount,
    productOldPrice: product.old_price?.value ?? 0,
    bundle_products: bundleProducts,
    title: product.product_name || "Product",
    checkoutWarrantyState: false,
    autoCharge: false,
    hasWarranty: false,
    warrantyState: false,
  };
}

export async function postUpsell(
  payload: CheckoutUpsellPayload,
): Promise<boolean> {
  const response = await fetch("/wp-json/checkout/v1/order/upsell", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  let data: CheckoutUpsellResponse | null = null;

  try {
    data = (await response.json()) as CheckoutUpsellResponse;
  } catch {
    data = null;
  }

  return Boolean(response.ok && data?.result === "SUCCESS");
}
